import { Link, useLocation, NavLink } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "../lib/AuthContext";
import { api } from "../lib/api";
import { useTodayLive, liveMode } from "../lib/useTodayLive";

/* Live folio + timestamp — clock ticks once/minute in IST */
function useIstNowLabel() {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 30000);
    return () => clearInterval(id);
  }, []);
  const opts = { timeZone: "Asia/Kolkata", day: "2-digit", month: "short", year: "numeric" };
  const timeOpts = { timeZone: "Asia/Kolkata", hour: "2-digit", minute: "2-digit", hour12: false };
  return {
    date: new Intl.DateTimeFormat("en-GB", opts).format(now).toUpperCase(),
    time: new Intl.DateTimeFormat("en-GB", timeOpts).format(now),
  };
}

/* Auto-incrementing folio number from the backend ledger */
function useFolio() {
  const [folio, setFolio] = useState(1);
  useEffect(() => {
    let cancelled = false;
    const load = () => api.get("/lives/folio").then((r) => {
      if (!cancelled) setFolio(Number(r.data?.folio || 1));
    }).catch(() => {});
    load();
    // Refresh every 60s so the ledger increments as drops go live
    const id = setInterval(load, 60000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);
  return folio;
}

export default function SiteHeader() {
  const { user } = useAuth();
  const loc = useLocation();
  const isAdmin = loc.pathname.startsWith("/admin");
  const isLiveRoom = loc.pathname.startsWith("/live/");
  const { date, time } = useIstNowLabel();
  const folio = useFolio();
  // Poll for the currently airing/next drop so we can flip the top strip when
  // a live goes on-air. Suppressed inside /live/:id (user is already there).
  const { live: todayLive } = useTodayLive({ intervalMs: 15000 });
  const mode = liveMode(todayLive);
  const showLiveStrip = !isLiveRoom && !isAdmin && todayLive?.live_id && ["live", "final", "preshow"].includes(mode);
  if (isAdmin) return null;

  return (
    <header
      className={`sticky top-0 z-40 border-b border-[#161616] bg-[#F8F7F2]/95 backdrop-blur ${isLiveRoom ? "hidden lg:block" : ""}`}
      data-testid="site-header"
    >
      {showLiveStrip && (
        <Link
          to={`/live/${todayLive.live_id}`}
          className="flex items-center justify-between gap-3 px-4 md:px-10 h-9 bg-[#FF3B30] text-white hover:bg-[#E1372F] transition-colors"
          data-testid="header-live-strip"
        >
          <span className="flex items-center gap-2 font-mono-ds text-[10px] md:text-[11px] uppercase tracking-[0.24em] font-bold">
            <span className={`inline-block w-2 h-2 rounded-full bg-white ${mode === "live" ? "animate-pulse" : ""}`} />
            {mode === "live" ? "Live Now" : mode === "final" ? "Final Window" : "Doors Open"}
            <span className="opacity-60 mx-1">·</span>
            <span>Drop #{String(todayLive.drop_no || 1).padStart(3, "0")}</span>
            {todayLive?.participants > 0 && (
              <>
                <span className="opacity-60 mx-1">·</span>
                <span className="tabular-nums">{Number(todayLive.participants).toLocaleString("en-IN")} inside</span>
              </>
            )}
          </span>
          <span className="font-mono-ds text-[10px] md:text-[11px] uppercase tracking-[0.24em] font-bold flex items-center gap-1">
            {mode === "final" ? "Lock Price" : "Join"}
            <span aria-hidden="true">→</span>
          </span>
        </Link>
      )}
      {/* ============== TOP FOLIO STRIP — metadata flanks ============== */}
      <div
        className="hidden md:flex items-center justify-between px-6 md:px-10 h-7 border-b border-[#E5E1D8] font-mono-ds text-[10px] uppercase tracking-[0.24em] text-[#6E6E6E]"
        data-testid="masthead-folio-strip"
      >
        <span data-testid="masthead-folio-left">
          <span className="text-[#264733] font-bold" data-testid="masthead-folio-number">
            Drop No. {String(folio).padStart(3, "0")}
          </span>
          <span className="opacity-40 mx-2">·</span>
          <span>{date}</span>
        </span>
        <span className="hidden lg:flex items-center gap-2 opacity-70">
          <span>Vol. 1 · Broadcast Ledger</span>
        </span>
        <span data-testid="masthead-folio-right">
          <span>9:00 PM IST · One Product</span>
          <span className="opacity-40 mx-2">·</span>
          <span className="text-[#264733] font-bold tabular-nums">{time} IST</span>
        </span>
      </div>

      {/* ============== MIDDLE MASTHEAD ROW ============== */}
      <div className="relative flex items-center justify-between px-4 md:px-10 h-16 md:h-20">
        {/* Left plate — D tile only (masthead is centered) */}
        <Link to="/" className="flex items-center gap-3 shrink-0" data-testid="site-logo">
          <div className="w-9 h-9 md:w-10 md:h-10 bg-[#264733] flex items-center justify-center font-display text-white text-xl md:text-2xl leading-none shadow-[2px_2px_0_#161616]">
            D
          </div>
          {/* Mobile-only compact wordmark */}
          <div className="md:hidden font-display text-lg uppercase tracking-tight whitespace-nowrap">
            Drop Shop <span className="text-[#FF6A00]">Live</span>
          </div>
        </Link>

        {/* Centered masthead — the newspaper wordmark */}
        <Link
          to="/"
          className="hidden md:block absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 whitespace-nowrap"
          data-testid="site-masthead"
          aria-label="Drop Shop Live — home"
        >
          <div className="font-display text-3xl md:text-[38px] lg:text-[44px] tracking-tight uppercase leading-none flex items-baseline gap-3">
            <span className="text-[#161616]">Drop Shop</span>
            <span className="text-[#6E6E6E] font-mono-ds text-lg tracking-[0.3em] font-bold">//</span>
            <span className="text-[#FF6A00] italic font-normal">Live</span>
          </div>
          <div className="mt-1 text-center font-mono-ds text-[9px] tracking-[0.32em] uppercase text-[#6E6E6E]">
            A Nightly Drop Ritual · Est. 2026
          </div>
        </Link>

        {/* Right plate — Sign-in / avatar */}
        <div className="flex items-center gap-3 shrink-0">
          {user ? (
            <Link to="/profile" className="flex items-center gap-2" data-testid="header-profile-link">
              <span className="hidden lg:inline font-mono-ds text-[11px] uppercase tracking-widest text-[#6E6E6E] max-w-[180px] truncate">
                {user.name || user.email}
              </span>
              <div className="w-10 h-10 border border-[#161616] overflow-hidden flex items-center justify-center hover:bg-[#161616] hover:text-white transition-colors bg-[#FFFFFF] shadow-[2px_2px_0_#161616]">
                {user.picture ? (
                  <img src={user.picture} alt="" className="w-full h-full object-cover" />
                ) : (
                  <span className="font-display text-base">
                    {(user.name || user.email || "?").slice(0, 1).toUpperCase()}
                  </span>
                )}
              </div>
            </Link>
          ) : (
            <Link to="/login" data-testid="header-login-link">
              <button className="btn-urgent h-10 px-5 text-[11px]" data-testid="header-signin-btn">
                Sign In →
              </button>
            </Link>
          )}
        </div>
      </div>

      {/* ============== NAV ROW ============== */}
      <nav
        className="hidden md:flex items-center justify-center gap-6 border-t border-[#161616] font-mono-ds text-[11px] uppercase tracking-[0.28em] h-10"
        data-testid="masthead-nav"
      >
        {user ? (
          <NavItem to="/profile" label="Profile" testId="nav-profile" />
        ) : (
          <Link to="/login" data-testid="nav-profile-signin" className="px-3 py-2 hover:text-[#FF6A00] transition-colors text-[#6E6E6E]">
            Profile
          </Link>
        )}
      </nav>

      {/* Mobile nav row */}
      <nav
        className="md:hidden flex items-center justify-center border-t border-[#161616] font-mono-ds text-[10px] uppercase tracking-widest h-10"
        data-testid="masthead-nav-mobile"
      >
        <MobileItem to={user ? "/profile" : "/login"} label="Profile" />
      </nav>
    </header>
  );
}

function NavItem({ to, label, testId }) {
  return (
    <NavLink
      to={to}
      data-testid={testId}
      className={({ isActive }) =>
        `px-3 py-2 transition-colors ${isActive ? "text-[#FF6A00]" : "text-[#6E6E6E] hover:text-[#FF6A00]"}`
      }
    >
      {label}
    </NavLink>
  );
}

function MobileItem({ to, label }) {
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        `flex-1 text-center py-2 ${isActive ? "text-[#FF6A00]" : "text-[#6E6E6E]"}`
      }
      data-testid={`mobile-nav-${label.toLowerCase()}`}
    >
      {label}
    </NavLink>
  );
}
